import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Star } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useScope } from '../../context/ScopeContext';
import { ScopeUser } from '../../types';

/**
 * "Viewing · Everyone ▾" — the one place the portal picks whose book it shows
 * (MAG-168). Lists the people core says the signed-in user may view as,
 * grouped by team, with each person's assignment count. Locked (no menu,
 * labelled "Your book") when the caller can see nobody but themselves.
 */
const ScopeControl: React.FC = () => {
  const { user } = useAuth();
  const { loading, all, users, pick, setPick, label } = useScope();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const groups = useMemo(() => {
    const byTeam = new Map<string, { name: string; users: ScopeUser[] }>();
    users.forEach((u) => {
      const key = u.team_id || '';
      if (!byTeam.has(key)) byTeam.set(key, { name: u.team_name || 'No team', users: [] });
      byTeam.get(key)!.users.push(u);
    });
    return [...byTeam.values()]
      .map((g) => ({ ...g, users: g.users.sort((a, b) => b.leads - a.leads) }))
      .sort((a, b) => b.users.reduce((n, u) => n + u.leads, 0) - a.users.reduce((n, u) => n + u.leads, 0));
  }, [users]);

  const choose = (id: string | null) => { setPick(id); setOpen(false); };
  const name = (u: ScopeUser) => u.display_name || u.name || u.id.slice(0, 8);
  const locked = !loading && !all;

  return (
    <div ref={root} className="relative mx-3 mt-2">
      <button
        type="button"
        disabled={locked || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg border-[1.5px] border-sketch text-left
                    text-[12.5px] font-bold ${locked ? 'bg-warm-white text-charcoal' : 'bg-sage/20 text-forest shadow-sketch-btn'}`}
      >
        <span className="min-w-0">
          <span className="block font-display text-[9.5px] font-semibold tracking-[0.13em] uppercase opacity-75">
            {locked ? 'Your book' : 'Viewing'}
          </span>
          <span className="block truncate leading-tight">{loading ? '…' : locked ? (user?.name || user?.email) : label}</span>
        </span>
        {!locked && <ChevronDown className="ml-auto w-3.5 h-3.5 flex-none" strokeWidth={2.2} />}
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full mt-1.5 z-30 max-h-[60vh] overflow-y-auto p-1.5 rounded-lg
                     border-[1.5px] border-sketch bg-warm-white shadow-sketch"
        >
          <button
            type="button"
            role="option"
            aria-selected={pick === null}
            onClick={() => choose(null)}
            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[13px] font-semibold text-left
                        hover:bg-sage/20 ${pick === null ? 'text-forest' : 'text-charcoal'}`}
          >
            <span className="w-[22px] h-[22px] rounded-full bg-gold text-charcoal grid place-items-center flex-none">
              <Star className="w-3 h-3" strokeWidth={2.5} />
            </span>
            {user?.role === 'admin' ? 'Everyone' : 'My team'}
            <span className="num ml-auto text-[11.5px] font-medium text-graphite">{users.length} people</span>
          </button>
          {groups.map((g) => (
            <React.Fragment key={g.name}>
              <div className="font-display text-[10px] font-semibold tracking-[0.13em] uppercase text-graphite px-2.5 pt-2.5 pb-0.5">
                {g.name}
              </div>
              {g.users.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  role="option"
                  aria-selected={pick === u.id}
                  onClick={() => choose(u.id)}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[13px] font-semibold text-left
                              hover:bg-sage/20 ${pick === u.id ? 'text-forest' : 'text-charcoal'}`}
                >
                  <span className="avat w-[22px] h-[22px] bg-forest text-warm-white text-[11px]">{name(u).charAt(0).toUpperCase()}</span>
                  <span className="truncate">{name(u)}{u.id === user?.id ? ' (you)' : ''}</span>
                  <span className="num ml-auto text-[11.5px] font-medium text-graphite">{u.leads}</span>
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScopeControl;
